import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from '../components/Helmet';
import { motion } from 'framer-motion';
import { MapPin, Mail, Clock, Calendar, MessageSquare } from 'lucide-react';
import ContactForm from '../components/ui/ContactForm';
import CalendlyWidget from '../components/CalendlyWidget';
import { openCalendlyPopup } from '../components/CalendlyWidget';

const ContactPage = () => {
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Open Calendly popup if 'book' parameter is present in URL
    if (searchParams.has('book')) {
      // Delay slightly to ensure Calendly script is loaded
      const timer = setTimeout(() => {
        openCalendlyPopup();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [searchParams]);
  return (
    <>
      <Helmet>
        <title>Contactez Upnov — Formation IA sur mesure pour entreprises</title>
        <meta
          name="description"
          content="Contactez Upnov pour organiser une formation IA en entreprise, un atelier métier ou un diagnostic IA gratuit. Réponse sous 24h. Basés à La Défense, intervenons partout en France."
        />
        <meta name="keywords" content="contact formation IA, devis formation IA, formation IA entreprise Paris, Upnov contact" />
        <meta property="og:title" content="Contactez Upnov — Formation IA sur mesure pour entreprises" />
        <meta property="og:description" content="Contactez Upnov pour organiser une formation IA en entreprise, un atelier métier ou un diagnostic IA gratuit. Réponse sous 24h." />
        <meta property="og:url" content="https://www.upnov.com/contact" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.upnov.com/equipe.webp" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
      </Helmet>
      {/* Hero Section */}
      <section className="bg-primary text-white py-16 md:py-24">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="heading-lg mb-6">Contactez-nous</h1>
            <p className="text-xl mb-8">
              Nous sommes à votre disposition pour répondre à toutes vos questions concernant
              nos formations en IA et pour vous accompagner dans votre projet.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#contact-form"
                className="inline-flex items-center justify-center gap-2 bg-white text-primary px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors"
              >
                <MessageSquare className="h-5 w-5" />
                Contactez-nous
              </a>
              <button
                onClick={openCalendlyPopup}
                className="inline-flex items-center justify-center gap-2 bg-transparent text-white border-2 border-white px-6 py-3 rounded-lg font-medium hover:bg-white/10 transition-colors"
              >
                <Calendar className="h-5 w-5" />
                Réservez une consultation
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-padding bg-white" id="contact-form">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <motion.div
              className="order-2 lg:order-1"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="heading-md mb-8">Nos coordonnées</h2>

              <div className="space-y-8 mb-12">
                <div className="flex items-start">
                  <div className="bg-primary/10 p-3 rounded-full mr-4">
                    <MapPin className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium text-lg mb-1">Adresse</h3>
                    <p className="text-gray-600">
                      Coeur Défense, 92400 Courbevoie, <strong>France</strong>
                    </p>

                    <p className="text-gray-600">
                      Quartier Européen, Bruxelles, <strong>Belgique</strong>
                    </p>

                    <p className="text-gray-600">
                      Centre Ville, Genève, <strong>Suisse</strong>
                    </p>
                  </div>
                </div>

{/*                 <div className="flex items-start">
                  <div className="bg-primary/10 p-3 rounded-full mr-4">
                    <Phone className="h-6 w-6 text-primary" />
                  </div>

                </div> */}

                <div className="flex items-start">
                  <div className="bg-primary/10 p-3 rounded-full mr-4">
                    <Mail className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium text-lg mb-1">Email</h3>
                    <p className="text-gray-600">info at upnov.com</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-primary/10 p-3 rounded-full mr-4">
                    <Clock className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium text-lg mb-1">Horaires d'ouverture</h3>
                    <p className="text-gray-600">Du lundi au vendredi, de 9h à 18h</p>
                  </div>
                </div>
              </div>

              <h3 className="font-medium text-lg mb-4">Suivez-nous</h3>
              <div className="flex space-x-4">
                <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="bg-gray-100 hover:bg-gray-200 p-3 rounded-full transition-colors">
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.48 10.52H14V7.5c0-1.1.9-2 2-2h2V2h-2c-3.31 0-6 2.69-6 6v2.5H6v3.5h4V22h4v-9.48h3.12l1.36-3.5z"></path>
                  </svg>
                </a>
                <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="bg-gray-100 hover:bg-gray-200 p-3 rounded-full transition-colors">
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M22.46 6.01c-.77.35-1.6.59-2.46.69.88-.53 1.56-1.37 1.88-2.38-.83.49-1.74.85-2.71 1.04-.78-.83-1.89-1.35-3.12-1.35-2.36 0-4.28 1.92-4.28 4.28 0 .33.04.66.11.97-3.56-.18-6.73-1.89-8.85-4.48-.37.64-.58 1.38-.58 2.16 0 1.49.75 2.81 1.9 3.58-.7-.02-1.37-.22-1.95-.54v.06c0 2.08 1.48 3.82 3.44 4.21-.36.1-.73.15-1.12.15-.27 0-.54-.03-.8-.08.54 1.7 2.12 2.94 4 2.98-1.46 1.15-3.31 1.83-5.32 1.83-.35 0-.69-.02-1.03-.06 1.9 1.22 4.15 1.93 6.57 1.93 7.88 0 12.2-6.53 12.2-12.2 0-.18 0-.37-.01-.55.84-.6 1.56-1.36 2.14-2.23z"></path>
                  </svg>
                </a>
                <a href="https://www.linkedin.com/company/upnov" target="_blank" rel="noopener noreferrer" className="bg-gray-100 hover:bg-gray-200 p-3 rounded-full transition-colors">
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19.04 3H4.96C3.88 3 3 3.88 3 4.96v14.08C3 20.12 3.88 21 4.96 21h14.08C20.12 21 21 20.12 21 19.04V4.96C21 3.88 20.12 3 19.04 3zM8.5 17h-2v-7h2v7zm-1-8.12c-.69 0-1.25-.56-1.25-1.25s.56-1.25 1.25-1.25 1.25.56 1.25 1.25-.56 1.25-1.25 1.25zM17 17h-2v-3.75c0-.75-.25-1.25-1.25-1.25s-1.5.5-1.5 1.25V17h-2v-7h2v1c.5-.75 1.25-1 2-1s1.5.25 2 .75 1 1.25 1 2.25V17z"></path>
                  </svg>
                </a>
                <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="bg-gray-100 hover:bg-gray-200 p-3 rounded-full transition-colors">
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9 9-4.03 9-9-4.03-9-9-9zm0 16.5c-4.14 0-7.5-3.36-7.5-7.5S7.86 4.5 12 4.5s7.5 3.36 7.5 7.5-3.36 7.5-7.5 7.5zm3.75-9c0-2.07-1.68-3.75-3.75-3.75S8.25 8.43 8.25 10.5s1.68 3.75 3.75 3.75 3.75-1.68 3.75-3.75zm-6 0c0-1.24 1.01-2.25 2.25-2.25s2.25 1.01 2.25 2.25-1.01 2.25-2.25 2.25S9.75 11.74 9.75 10.5z"></path>
                  </svg>
                </a>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="order-1 lg:order-2 bg-gray-50 p-8 rounded-xl shadow-sm"
            >
              <h2 className="text-2xl font-semibold mb-6">Envoyez-nous un message</h2>
              <ContactForm />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="bg-gray-50 py-16">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="heading-md mb-6">Nous trouver</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Nos locaux sont idéalement situés en plein cœur de Paris, facilement accessibles
              en transport en commun.
            </p>
          </div>

          <div className="h-[600px] max-w-6xl mx-auto rounded-xl overflow-hidden">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2623.2913160546304!2d2.2419825764662793!3d48.89078507133658!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e66505ae49f911%3A0x6a1cb8a5a4b0119a!2sTour%20Coeur%20D%C3%A9fense%2C%2092400%20Courbevoie%2C%20France!5e0!3m2!1sfr!2sil!4v1747745995502!5m2!1sfr!2sil"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen={true}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="heading-md mb-6">Questions fréquentes</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Consultez nos réponses aux questions les plus fréquemment posées.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-gray-50 p-6 rounded-xl">
              <h3 className="text-xl font-semibold mb-3">Comment se déroulent vos formations ?</h3>
              <p className="text-gray-600">
                Nos formations alternent théorie et pratique, avec des exercices concrets adaptés au contexte
                professionnel. Elles sont disponibles en présentiel dans vos ou nos locaux mais aussi à distance ou en format hybride.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl">
              <h3 className="text-xl font-semibold mb-3">Quels profils peuvent suivre vos formations en IA ?</h3>
              <p className="text-gray-600">
                Nos formations s’adressent à tous les profils :

                Dirigeants et managers souhaitant intégrer l’IA dans leur stratégie,

                Équipes métiers (RH, finance, marketing, etc.) cherchant à identifier des cas d’usage,

                Collaborateurs non-techniques voulant comprendre les bases de l’IA et ses impacts.
                Aucune compétence technique préalable n’est nécessaire pour la majorité de nos formats.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl">
              <h3 className="text-xl font-semibold mb-3">Comment financer la formation ?</h3>
              <p className="text-gray-600">
                Nous pouvons vous accompagner dans la recherche de financements pour faciliter la mise en place de la formation.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl">
              <h3 className="text-xl font-semibold mb-3">Proposez-vous des formations sur mesure ?</h3>
              <p className="text-gray-600">
                Absolument. Nous adaptons nos programmes aux besoins spécifiques de votre entreprise et
                au niveau de compétence de vos équipes.
              </p>
            </div>
          </div>
        </div>
      </section>

      <CalendlyWidget />
    </>
  );
};

export default ContactPage;